import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Trash2,
  Edit,
  Search,
  RefreshCw,
  User,
  FileText,
  Timer,
  X,
  Download,
  ChevronUp,
  ChevronDown,
  ArrowUpDown
} from 'lucide-react';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import { usePermissions } from '../../../hooks/usePermissions';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';

import { API_BASE_URL } from '../../../api/config'; // 🟢 Centralized API_BASE_URL import (fixed relative path)
import Button from '../../../components/common/Button/Button';

import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const NoDues = ({ onBack, userRole = 'super_admin' }) => {
  const { hasPermission, canCreate, canUpdate, canDelete } = usePermissions();
  const queryClient = useQueryClient();
  const [showClearanceForm, setShowClearanceForm] = useState(false);
  const [selectedClearance, setSelectedClearance] = useState(null);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterStatus: 'all',
    filterDepartment: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: noDuesFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateNoDuesFilter,
    updatePage: updateNoDuesPage,
    updateLimit: updateNoDuesLimit,
    resetFilters: resetNoDuesFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;
  const debouncedSearchTerm = searchTerm; // Hook handles debouncing internally

  const statusFilter = noDuesFilters.filterStatus || 'all';
  const setStatusFilter = useCallback((status) => {
    updateNoDuesFilter({ filterStatus: status });
  }, [updateNoDuesFilter]);

  const departmentFilter = noDuesFilters.filterDepartment || 'all';
  const setDepartmentFilter = useCallback((department) => {
    updateNoDuesFilter({ filterDepartment: department });
  }, [updateNoDuesFilter]);

  const startDate = noDuesFilters.filterStartDate || '';
  const endDate = noDuesFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateNoDuesFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateNoDuesFilter]);

  const setStartDate = useCallback((date) => {
    updateNoDuesFilter({ filterStartDate: date });
  }, [updateNoDuesFilter]);

  const setEndDate = useCallback((date) => {
    updateNoDuesFilter({ filterEndDate: date });
  }, [updateNoDuesFilter]);

  const clearancesPage = noDuesFilters.page;
  const clearancesPageSize = noDuesFilters.limit;

  const setClearancesPage = useCallback((page) => {
    updateNoDuesPage(page);
  }, [updateNoDuesPage]);

  const setClearancesPageSize = useCallback((limit) => {
    updateNoDuesLimit(limit);
  }, [updateNoDuesLimit]);

  const [clearancesTotalPages, setClearancesTotalPages] = useState(1);
  const [clearancesTotalItems, setClearancesTotalItems] = useState(0);

  const [lwdError, setLwdError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [isEmployeeDropdownOpen, setIsEmployeeDropdownOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [editingClearanceId, setEditingClearanceId] = useState(null);
  const [noDuesTouchedFields, setNoDuesTouchedFields] = useState(new Set());
  const [lastUpdated, setLastUpdated] = useState(null);

  // Export State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Permission-based access control
  const canCreateClearance = hasPermission('exit_management', 'create');
  const canViewAllClearances = hasPermission('exit_management', 'read');
  const canEditClearance = hasPermission('exit_management', 'update'); // Enabled for users with update permission
  const canDeleteClearance = hasPermission('exit_management', 'delete');
  const canApproveClearance = hasPermission('exit_management', 'update');

  // Form state
  const [clearanceForm, setClearanceForm] = useState({
    employeeId: '',
    exitRequestId: '',
    lastWorkingDate: '',
    clearanceType: 'resignation',
    clearanceItems: [],
    priority: 'medium'
  });

  // Helper function for text input key press (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    const char = e.key;
    if (!/^[a-zA-Z0-9\s,]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
      e.preventDefault();
    }
  };

  // Helper function for number input key down (positive numbers only, 0 and greater)
  const handleNumberKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys, and numbers
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow numbers on top of keyboard
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow numbers on numpad
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Block: minus, plus, e, E, period (for decimal)
    if (e.keyCode === 189 || e.keyCode === 187 || e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
      e.preventDefault();
    }
  };

  // Helper function for search input key press (alphanumeric, spaces, and email special characters)
  const handleSearchKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleNoDuesFieldBlur = (field) => {
    setNoDuesTouchedFields((prev) => {
      const newSet = new Set(prev);
      newSet.add(field);
      return newSet;
    });
  };

  const validateField = (field, value, options = {}) => {
    switch (field) {
      case 'employeeId':
        return value ? '' : 'Employee is required';
      case 'exitRequestId':
        return value ? '' : 'Exit Request is required';
      case 'lastWorkingDate':
        if (!value) return 'Last Working Date is required';
        if (isNaN(new Date(value).getTime())) return 'Last Working Date must be a valid date';
        return '';
      case 'clearanceType':
        return value ? '' : 'Clearance Type is required';
      case 'itemName':
        if (!value || !value.trim()) return 'Item name is required';
        if (value.trim().length > 100) return 'Item name must be less than 100 characters';
        return '';
      case 'category':
        return value ? '' : 'Category is required';
      default:
        return '';
    }
  };

  const validateAll = () => {
    const errs = {};
    const set = (k, v) => { if (v) errs[k] = v; };
    set('employeeId', validateField('employeeId', clearanceForm.employeeId));
    set('exitRequestId', validateField('exitRequestId', clearanceForm.exitRequestId));
    set('lastWorkingDate', validateField('lastWorkingDate', clearanceForm.lastWorkingDate));
    set('clearanceType', validateField('clearanceType', clearanceForm.clearanceType));

    if (Array.isArray(clearanceForm.clearanceItems)) {
      clearanceForm.clearanceItems.forEach((it, idx) => {
        set(`clearanceItems.${idx}.category`, validateField('category', it.category));
        set(`clearanceItems.${idx}.itemName`, validateField('itemName', it.itemName));
        if (it.itemValue != null && Number(it.itemValue) < 0) errs[`clearanceItems.${idx}.itemValue`] = 'Value cannot be negative';
      });
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // --- React Query Implementations ---

  // 1. Fetch Clearances (Paginated & Filtered)
  const {
    data: clearancesData,
    isLoading: isClearancesLoading,
    refetch: refetchClearances
  } = useQuery({
    queryKey: ['clearances', clearancesPage, clearancesPageSize, statusFilter, departmentFilter, searchTerm, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(clearancesPage));
      params.set('limit', String(clearancesPageSize));
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (departmentFilter && departmentFilter !== 'all') params.set('department', departmentFilter);
      if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await fetch(`${API_BASE_URL}/no-dues-real?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });

      if (!response.ok) throw new Error('Failed to load clearances');
      const result = await response.json();
      return result.data || {};
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchInterval: 0,
  });

  const clearances = clearancesData?.clearances || [];

  // Update pagination state when data successfully loads
  useEffect(() => {
    if (clearancesData?.pagination) {
      setClearancesTotalPages(clearancesData.pagination.totalPages || 1);
      setClearancesTotalItems(clearancesData.pagination.totalItems || 0);
      setLastUpdated(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }));
    }
  }, [clearancesData]);

  // 2. Fetch Statistics
  const { data: statsData } = useQuery({
    queryKey: ['clearanceStats'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/no-dues-real/stats/dashboard`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) throw new Error('Failed to load statistics');
      const result = await response.json();
      return result.data;
    },
    staleTime: 0,
    refetchInterval: 0,
  });

  const stats = statsData || {
    totalClearances: 0,
    pendingClearances: 0,
    inProgressClearances: 0,
    completedClearances: 0,
    rejectedClearances: 0
  };

  // 3. Fetch Employees
  const { data: employeesData } = useQuery({
    queryKey: ['employees', 'approved'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/no-dues-simple/employees?status=approved`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) throw new Error('Failed to load employees');
      const result = await response.json();
      return result.data.employees || [];
    },
    staleTime: 0
  });

  const employees = employeesData || [];

  // 4. Fetch Departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/hr/departments?page=1&limit=10&sortBy=createdAt&sortOrder=desc&isActive=all&paginated=true`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        mode: 'cors'
      });
      if (!response.ok) {
        if (response.status === 401) {
          // Optionally redirect to login or handle session expiry
        }
        throw new Error('Failed to load departments');
      }
      const result = await response.json();
      const raw = result?.data?.docs || result?.data?.departments || result?.data || result?.departments || result || [];
      const names = (Array.isArray(raw) ? raw : [])
        .map((d) => (typeof d === 'string' ? d : d?.name || d?.departmentName || d?.label || d?.value))
        .filter(Boolean);
      return Array.from(new Set(names));
    },
    staleTime: 0
  });

  const departments = departmentsData || [];

  // 5. Fetch Exit Requests
  const { data: exitRequestsData } = useQuery({
    queryKey: ['exitRequests'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/no-dues-simple/exit-requests`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) throw new Error('Failed to load exit requests');
      const result = await response.json();
      return result.data.exitRequests || [];
    },
    staleTime: 0
  });

  const exitRequests = exitRequestsData || [];

  // Mutations

  // Create Clearance Mutation
  const createClearanceMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await fetch(`${API_BASE_URL}/no-dues-simple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    },
    onSuccess: () => {
      alert('✅ No-dues clearance created successfully!');
      setShowClearanceForm(false);
      resetClearanceForm();
      queryClient.invalidateQueries(['clearances']);
      queryClient.invalidateQueries(['clearanceStats']);
    },
    onError: (error) => {
      setFormErrors((prev) => ({ ...prev, submit: error.message }));
    }
  });

  // Approve Clearance Mutation
  const approveClearanceMutation = useMutation({
    mutationFn: async ({ clearanceId, currentUser }) => {
      const response = await fetch(`${API_BASE_URL}/no-dues-real/${clearanceId}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          approvedBy: currentUser.id,
          approvedByName: currentUser.name,
          approvalComments: `Approved by ${currentUser.name}`
        })
      });
      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Unknown error');
      }
      return await response.json();
    },
    onSuccess: (data, variables) => {
      alert(`✅ Clearance approved by ${variables.currentUser.name}!`);
      queryClient.invalidateQueries(['clearances']);
      queryClient.invalidateQueries(['clearanceStats']);
    },
    onError: (error) => {
      alert('❌ Failed to approve clearance: ' + error.message);
    }
  });

  // Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ clearanceId, newStatus, currentUser }) => {
      const response = await fetch(`${API_BASE_URL}/no-dues-real/${clearanceId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          updatedBy: currentUser.id,
          updatedByName: currentUser.name,
          comments: `Status updated to ${newStatus} by ${currentUser.name}`
        })
      });
      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Unknown error');
      }
      return await response.json();
    },
    onSuccess: (data, variables) => {
      alert(`✅ Clearance status updated to ${variables.newStatus} by ${variables.currentUser.name}!`);
      queryClient.invalidateQueries(['clearances']);
      queryClient.invalidateQueries(['clearanceStats']);
      // If details view is open for this clearance, refresh its data from latest list (or fetch it)
      // Here we assume simply closing or letting the list update is enough, 
      // but to update the selected view we might need to manually update state or refetch individual
      setSelectedClearance((prev) => {
        if (!prev) return prev;
        const same = (prev._id || prev.id) === variables.clearanceId;
        if (!same) return prev;
        // Optimization: use the returned data if available, or just partial update
        return { ...prev, clearanceStatus: variables.newStatus };
      });
    },
    onError: (error) => {
      alert('❌ Failed to update clearance status: ' + error.message);
    }
  });

  // Update Clearance Mutation
  const updateClearanceMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      const response = await fetch(`${API_BASE_URL}/no-dues-real/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    },
    onSuccess: () => {
      alert('✅ No-dues clearance updated successfully!');
      setShowClearanceForm(false);
      resetClearanceForm();
      queryClient.invalidateQueries(['clearances']);
      queryClient.invalidateQueries(['clearanceStats']);
    },
    onError: (error) => {
      setFormErrors((prev) => ({ ...prev, submit: error.message }));
    }
  });

  // Delete Clearance Mutation
  const deleteClearanceMutation = useMutation({
    mutationFn: async (clearanceId) => {
      const response = await fetch(`${API_BASE_URL}/no-dues-real/${clearanceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      });
      if (!response.ok) {
        const errorResult = await response.json();
        // Handle specific status codes if needed via error properties
        const err = new Error(errorResult.message || `HTTP ${response.status}: ${response.statusText}`);
        err.status = response.status;
        throw err;
      }
      return await response.json();
    },
    onSuccess: () => {
      alert('✅ Clearance deleted successfully!');
      queryClient.invalidateQueries(['clearances']);
      queryClient.invalidateQueries(['clearanceStats']);
      setPendingDeleteId(null);
    },
    onError: (error) => {
      if (error.status === 400) {
        alert('❌ Cannot delete this clearance: ' + (error.message || 'Only pending clearances can be deleted'));
      } else if (error.status === 404) {
        alert('❌ Clearance not found: ' + (error.message || 'The clearance may have already been deleted'));
      } else {
        alert('❌ Failed to delete clearance: ' + error.message);
      }
    }
  });

  // Create clearance
  const handleSubmitClearance = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      setNoDuesTouchedFields(new Set([
        'employeeId', 'exitRequestId', 'lastWorkingDate', 'clearanceType',
        ...clearanceForm.clearanceItems.flatMap((_, idx) => [
          `clearanceItems.${idx}.category`,
          `clearanceItems.${idx}.itemName`,
          `clearanceItems.${idx}.itemValue`
        ])
      ]));
      return;
    }

    // Find the selected employee to get the employeeId
    const selectedEmployee = employees.find(emp => emp._id === clearanceForm.employeeId);
    if (!selectedEmployee) {
      setFormErrors((prev) => ({ ...prev, employeeId: 'Selected employee not found' }));
      return;
    }
    // Ensure an exit request is chosen; if not, pick the most recent for the selected employee
    let exitRequestIdToUse = clearanceForm.exitRequestId;
    if (!exitRequestIdToUse) {
      const code = selectedEmployee.employeeId || selectedEmployee.employmentInfo?.employeeId;
      const dbid = String(selectedEmployee._id || '');
      const name = (selectedEmployee.fullName || `${selectedEmployee.personalInfo?.firstName || ''} ${selectedEmployee.personalInfo?.lastName || ''}`).trim().toLowerCase();
      const candidates = (exitRequests || []).filter((r) => {
        const rCode = r.employeeId || r.employeeCode || r.employee?.employeeId || r.employee?.employeeCode;
        const rDbId = String(r.employeeDbId || r.employee?._id || r.employee || r.employeeMongoId || '');
        const rName = (r.employeeName || '').toLowerCase().trim();
        return (code && rCode && code === rCode) || (dbid && rDbId && dbid === rDbId) || (name && rName && name === rName);
      });
      if (candidates.length > 0) {
        const chosen = candidates
          .filter((r) => extractLastWorkingDate(r))
          .sort((a, b) => new Date(extractLastWorkingDate(b)) - new Date(extractLastWorkingDate(a)))[0] || candidates[0];
        exitRequestIdToUse = chosen?._id;
      }
    }
    if (!exitRequestIdToUse) {
      setFormErrors((prev) => ({ ...prev, exitRequestId: 'Please select an Exit Request' }));
      return;
    }
    // Prefer auto-filled date from the selected exit request if form value is empty
    let lastWorkingDateToUse = clearanceForm.lastWorkingDate;
    if (!lastWorkingDateToUse) {
      const exitReq = (exitRequests || []).find((r) => (r._id || r.id) === exitRequestIdToUse);
      lastWorkingDateToUse = extractLastWorkingDate(exitReq);
    }
    // Do not block on frontend; rely on resolved or backend validation

    try {

      // Prepare form data with correct employeeId
      const formData = {
        ...clearanceForm,
        employeeId: selectedEmployee.employeeId, // Use the employeeId field instead of _id
        lastWorkingDate: lastWorkingDateToUse,
        exitRequestId: exitRequestIdToUse
      };

      // Use mutation
      if (editingClearanceId) {
        updateClearanceMutation.mutate({ id: editingClearanceId, formData });
      } else {
        createClearanceMutation.mutate(formData);
      }

    } catch (error) {
      setFormErrors((prev) => ({ ...prev, submit: error.message }));
    } finally {
      // Handled in mutation callbacks
    }
  };

  // Edit functionality
  const handleEditClearance = (clearance) => {
    // Find the employee in our local list to get the _id for the dropdown
    const emp = (employees || []).find(e => {
      const eDbId = String(e._id || e.id || '').toLowerCase();
      const eCode = String(e.employeeId || e.employmentInfo?.employeeId || '').toLowerCase();
      const eName = (e.fullName || `${e.personalInfo?.firstName || ''} ${e.personalInfo?.lastName || ''}`).trim().toLowerCase();

      // Check all possible locations for the clearance employee ID or code
      const cDbId = String(clearance.employeeDbId || clearance.employeeId?._id || (typeof clearance.employeeId === 'string' ? clearance.employeeId : '') || clearance.employee?._id || clearance.employee || '').toLowerCase();
      const cCode = String(clearance.employeeId?.employeeId || clearance.employeeCode || (typeof clearance.employeeId === 'string' ? clearance.employeeId : '') || '').toLowerCase();
      const cName = (clearance.employeeName || '').trim().toLowerCase();

      return (eDbId && cDbId && eDbId === cDbId) ||
        (eCode && cCode && eCode === cCode) ||
        (eName && cName && eName === cName);
    });

    setClearanceForm({
      employeeId: emp?._id || '',
      exitRequestId: clearance.exitRequestId || clearance.exitRequest?._id || clearance.exitRequest || '',
      lastWorkingDate: clearance.lastWorkingDate ? new Date(clearance.lastWorkingDate).toISOString().split('T')[0] : '',
      clearanceType: (clearance.clearanceType || 'resignation').toLowerCase(),
      clearanceItems: (clearance.clearanceItems || []).map(item => ({
        category: item.category,
        itemName: item.itemName,
        itemValue: item.itemValue || 0,
        itemSerialNumber: item.itemSerialNumber || '',
        itemCondition: item.itemCondition || 'good',
        status: item.status || 'pending'
      })),
      priority: (clearance.priority || 'medium').toLowerCase()
    });

    setEditingClearanceId(clearance._id || clearance.id);
    setShowClearanceForm(true);
    setNoDuesTouchedFields(new Set());
    setFormErrors({});
  };

  // Approve clearance
  const handleApproveClearance = async (clearanceId) => {
    const currentUser = {
      id: '507f1f77bcf86cd799439011',
      name: 'HR Manager',
      role: userRole
    };
    approveClearanceMutation.mutate({ clearanceId, currentUser });
  };

  // Update clearance status
  const handleStatusUpdate = async (clearanceId, newStatus) => {
    const currentUser = {
      id: '507f1f77bcf86cd799439011',
      name: 'HR Manager',
      role: userRole
    };
    updateStatusMutation.mutate({ clearanceId, newStatus, currentUser });
  };
  // Delete clearance
  const handleDeleteClearance = async (clearanceId, skipConfirm = false) => {
    if (!skipConfirm) {
      if (!window.confirm('Are you sure you want to delete this clearance? This action cannot be undone.')) return;
    }
    deleteClearanceMutation.mutate(clearanceId);
  };

  // Bulk delete clearances handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.size} clearance(s)? This action cannot be undone.`)) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedRows);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Process deletions in parallel batches of 5 to speed up but not overwhelm
      const batchSize = 5;
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        await Promise.all(batch.map(async (id) => {
          try {
            const response = await fetch(`${API_BASE_URL}/no-dues-real/${id}`, {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            if (response.ok) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }));
      }

      if (successCount > 0) {
        alert(`Successfully deleted ${successCount} clearance(s)`);
        setSelectedRows(new Set());
        queryClient.invalidateQueries(['clearances']);
        queryClient.invalidateQueries(['clearanceStats']);
      }

      if (errorCount > 0) {
        alert(`Failed to delete ${errorCount} clearance(s). Please try again.`);
      }
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // View clearance details
  const handleViewClearance = (clearance) => {
    // Always pick the freshest object from state by id so status is up-to-date
    const current = (clearances || []).find((c) => (c._id || c.id) === (clearance._id || clearance.id)) || clearance;
    setSelectedClearance(current);
    // setActiveTab('details'); // COMMENTED OUT - Dashboard removed
  };

  // --- Export Functionality ---

  // Handle row selection
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredClearances.map(c => c._id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (clearance) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(clearance._id)) {
      newSelected.delete(clearance._id);
    } else {
      newSelected.add(clearance._id);
    }
    setSelectedRows(newSelected);
  };

  // Map data for local export
  const mapClearanceForExport = (clearancesToExport) => {
    return clearancesToExport.map(c => ({
      'Clearance ID': c.clearanceId || '',
      'Employee Name': c.employeeName || '',
      'Email': c.employeeEmail || '',
      'Department': c.department || '',
      'Position': c.position || '',
      'Clearance Type': c.clearanceType || '',
      'Last Working Date': c.lastWorkingDate ? formatDate(c.lastWorkingDate, { includeTime: false }) : '',
      'Status': c.clearanceStatus || '',
      'Priority': c.priority || '',
      'Created Date': c.createdAt ? formatDate(c.createdAt, { includeTime: false }) : '',
      'Approved By': c.clearanceApproval?.approvedByName || '',
      'Approved Date': c.clearanceApproval?.approvedDate ? formatDate(c.clearanceApproval.approvedDate, { includeTime: false }) : ''
    }));
  };

  // Handle Export Button Click
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      handleExportSelected();
    } else {
      setShowExportModal(true);
    }
  };

  // 1. Export Selected Rows (Local)
  const handleExportSelected = () => {
    const selectedData = filteredClearances.filter(c => selectedRows.has(c._id));
    if (selectedData.length === 0) {
      alert("No rows selected for export.");
      return;
    }
    const csvData = mapClearanceForExport(selectedData);
    exportToCSV(csvData, 'no_dues_selected', { title: 'No-Dues Clearance Report', type: 'Selected Rows' });
    setSelectedRows(new Set()); // Clear selection
  };

  // 2. Export Current Page (Local)
  const handleExportCurrentPage = () => {
    if (filteredClearances.length === 0) {
      alert("No data available on the current page to export.");
      return;
    }
    const csvData = mapClearanceForExport(filteredClearances);
    exportToCSV(csvData, 'no_dues_page', { title: 'No-Dues Clearance Report', type: 'Current Page Data' });
    setShowExportModal(false);
  };

  // 3. Export Date Range (Backend)
  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('startDate', startDate);
      queryParams.append('endDate', endDate);
      queryParams.append('type', `Date Range (${startDate} to ${endDate})`);

      // Add current filters if needed? Usually date range export ignores current page filters but might respect status/dept
      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (departmentFilter !== 'all') queryParams.append('department', departmentFilter);
      if (searchTerm) queryParams.append('search', searchTerm);

      await downloadFromBackend(
        `${API_BASE_URL}/no-dues-real/export?${queryParams.toString()}`,
        generateExportFilename('no_dues_range'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
    } catch (error) {
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  // 4. Export All Data (Backend)
  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', 'All Data');

      // Respect current filters for "All Data" export? Usually yes.
      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (departmentFilter !== 'all') queryParams.append('department', departmentFilter);
      if (searchTerm) queryParams.append('search', searchTerm);

      await downloadFromBackend(
        `${API_BASE_URL}/no-dues-real/export?${queryParams.toString()}`,
        generateExportFilename('no_dues_all'),
        { 'x-user-role': userRole }
      );
      setShowExportModal(false);
    } catch (error) {
      alert("Failed to export data. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };



  // Edit functionality disabled - clearances cannot be edited once created

  // Reset form
  const resetClearanceForm = () => {
    setClearanceForm({
      employeeId: '',
      exitRequestId: '',
      lastWorkingDate: '',
      clearanceType: 'resignation',
      clearanceItems: [],
      priority: 'medium'
    });
    setFormErrors({});
    setNoDuesTouchedFields(new Set());
    setEditingClearanceId(null);
  };

  // Force refresh all data
  const forceRefreshAllData = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries(); // Invalidate all queries
    } catch (error) {
    } finally {
      setIsRefreshing(false);
    }
  };



  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="h-4 w-4" />,
      in_progress: <Timer className="h-4 w-4" />,
      completed: <CheckCircle className="h-4 w-4" />,
      rejected: <XCircle className="h-4 w-4" />
    };
    return icons[status] || <AlertCircle className="h-4 w-4" />;
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedClearances = useMemo(() => {
    if (!sortConfig.key) return clearances;

    return [...clearances].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [clearances, sortConfig]);

  // Filter clearances (Client-side redundant filtering if backend handles it, but keeping for compatibility)
  // Since we pass searchTerm to backend, 'clearances' should already be filtered.
  // Exception: local 'selectedRows' logic in exports might rely on this.
  const filteredClearances = sortedClearances; // Use sortedClearances here

  // Derived lists for the form behavior
  // Build robust index of exit requests by both employee code and employee DB id
  const exitEmpCodes = new Set(
    (exitRequests || [])
      .map((r) => r.employeeId || r.employeeCode || r.employee?.employeeId || r.employee?.employeeCode)
      .filter(Boolean)
  );
  const exitEmpDbIds = new Set(
    (exitRequests || [])
      .map((r) => r.employeeDbId || r.employee?._id || r.employee || r.employeeMongoId)
      .filter(Boolean)
      .map(String)
  );
  const exitEmpNames = new Set(
    (exitRequests || [])
      .map((r) => (r.employeeName || '').toLowerCase().trim())
      .filter(Boolean)
  );

  // Only employees who have at least one exit request (match by either code or DB id)
  let eligibleEmployees = (employees || []).filter((emp) => {
    const code = emp.employeeId || emp.employmentInfo?.employeeId;
    const dbid = String(emp._id || emp.id || '');
    const name = (emp.fullName || `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`)
      .trim()
      .toLowerCase();
    return (
      (code && exitEmpCodes.has(code)) ||
      (dbid && exitEmpDbIds.has(dbid)) ||
      (name && exitEmpNames.has(name))
    );
  });
  // Fallback: if nothing matched but we have data, show all employees rather than an empty list
  if (eligibleEmployees.length === 0 && (employees || []).length > 0 && (exitRequests || []).length > 0) {
    eligibleEmployees = employees;
  }

  // Filter exit requests for the selected employee (match by either code or DB id, fallback by name)
  const selectedEmployee = (employees || []).find((emp) => String(emp._id || emp.id || '') === String(clearanceForm.employeeId || ''));
  const selectedCode = selectedEmployee?.employeeId || selectedEmployee?.employmentInfo?.employeeId;
  const selectedDbId = selectedEmployee?._id ? String(selectedEmployee._id) : '';
  const selectedName = selectedEmployee?.fullName || `${selectedEmployee?.personalInfo?.firstName || ''} ${selectedEmployee?.personalInfo?.lastName || ''}`.trim();
  const exitRequestsForSelected = selectedEmployee
    ? (exitRequests || []).filter((r) => {
      const rCode = r.employeeId || r.employeeCode || r.employee?.employeeId || r.employee?.employeeCode;
      const rDbId = r.employeeDbId || r.employee?._id || r.employee || r.employeeMongoId;
      const rName = (r.employeeName || '').toLowerCase().trim();
      return (
        (selectedCode && rCode && selectedCode === rCode) ||
        (selectedDbId && rDbId && String(selectedDbId) === String(rDbId)) ||
        (selectedName && rName && selectedName.toLowerCase().trim() === rName)
      );
    })
    : [];

  // Find the currently selected exit request object
  const selectedExitRequest = (exitRequests || []).find((r) => (r._id || r.id) === clearanceForm.exitRequestId);

  // Helper: extract Last Working Date from various possible keys in exit request
  const extractLastWorkingDate = (er) => {
    if (!er) return '';
    const candidates = [
      er.lastWorkingDate,
      er?.exitDetails?.lastWorkingDate,
      er?.meta?.lastWorkingDate,
      er?.dateOfExit,
      er.lastWorkingdate,
      er.last_working_date,
      er.lastWorkingDay,
      er.lastWorkDate,
      er.LWD,
      er.lwd,
      er.finalWorkingDate,
      er.final_working_date
    ].filter(Boolean);
    if (candidates.length === 0) return '';
    const s = String(candidates[0]).trim();
    // If it's already likely a YYYY-MM-DD string
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    // If it's a timestamp number
    if (/^\d{10,13}$/.test(s)) {
      const ts = s.length === 13 ? Number(s) : Number(s) * 1000;
      const d = new Date(ts);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    }
    // Handle DD/MM/YYYY or MM/DD/YYYY (assume DD/MM/YYYY for India if ambiguous)
    if (/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/.test(s)) {
      const sep = s.includes('/') ? '/' : '-';
      const [p1, p2, y] = s.split(sep).map((x) => x.padStart(2, '0'));
      const dd = parseInt(p1, 10);
      const mm = parseInt(p2, 10);
      const isClearlyMDY = p1.length === 2 && p2.length === 2 && dd <= 12 && mm > 12; // 06/25/2026
      const day = isClearlyMDY ? mm : dd;
      const month = isClearlyMDY ? dd : mm;
      const ymd = `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      // Validate
      const d = new Date(ymd);
      if (!isNaN(d.getTime())) return ymd;
    }
    // Try generic Date parse last
    try {
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch (_) { }
    return '';
  };

  // Auto-fill last working date from the selected exit request
  useEffect(() => {
    if (selectedExitRequest) {
      const ymd = extractLastWorkingDate(selectedExitRequest);
      if (ymd && clearanceForm.lastWorkingDate !== ymd) {
        setClearanceForm((prev) => ({ ...prev, lastWorkingDate: ymd }));
        setLwdError('');
      } else if (!ymd) {
        setLwdError('Last Working Date not found in selected Exit Request');
      }
    }
  }, [selectedExitRequest?._id, selectedExitRequest?.lastWorkingDate]);

  // Auto-select exit request when employee is chosen
  // - If exactly one: pick that one
  // - If multiple: pick the most recent by lastWorkingDate
  useEffect(() => {
    if (selectedEmployee && exitRequestsForSelected.length > 0) {
      let chosen = exitRequestsForSelected[0];
      if (exitRequestsForSelected.length > 1) {
        chosen = [...exitRequestsForSelected]
          .filter((r) => r && (r.lastWorkingDate || r.lastWorkingdate || r.last_working_date))
          .sort((a, b) => new Date(extractLastWorkingDate(b)) - new Date(extractLastWorkingDate(a)))[0] || exitRequestsForSelected[0];
      }
      const chosenId = chosen?._id || chosen?.id;
      if (chosenId && clearanceForm.exitRequestId !== chosenId) {
        setClearanceForm((prev) => ({ ...prev, exitRequestId: chosenId }));
        fetchExitRequestAndSetLWD(chosenId);
      }
    }
  }, [selectedEmployee?._id, exitRequestsForSelected.length]);

  // Fetch full exit request by id and set LWD from backend
  const fetchExitRequestAndSetLWD = async (id) => {
    try {
      if (!id) return;
      const res = await fetch(`${API_BASE_URL}/exit-test/${id}`);
      const json = await res.json();
      const req = json?.data?.exitRequest || json?.data || json?.exitRequest || json;
      const ymd = extractLastWorkingDate(req);
      if (ymd) {
        setClearanceForm((prev) => ({ ...prev, lastWorkingDate: ymd }));
        setLwdError('');
      } else {
        setLwdError('Last Working Date missing in Exit Request data');
      }
    } catch (err) {
      setLwdError('Last Working Date missing in Exit Request data');
    }
  };

  // When user manually picks an Exit Request, immediately normalize and set LWD
  const handleExitRequestChange = (e) => {
    const id = e.target.value;
    setClearanceForm((prev) => ({ ...prev, exitRequestId: id }));
    fetchExitRequestAndSetLWD(id);
  };

  // Render clearance form
  const employeeDropdownRef = React.useRef(null);
  useEffect(() => {
    const onDocClick = (e) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        if (isEmployeeDropdownOpen) {
          handleNoDuesFieldBlur('employeeId');
          setFormErrors((prev) => ({ ...prev, employeeId: validateField('employeeId', clearanceForm.employeeId) }));
        }
        setIsEmployeeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isEmployeeDropdownOpen, clearanceForm.employeeId]);

  const renderClearanceForm = () => (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {editingClearanceId ? 'Edit No-Dues Clearance' : 'New No-Dues Clearance'}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setShowClearanceForm(false);
                resetClearanceForm();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmitClearance} noValidate className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div ref={employeeDropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => !editingClearanceId && setIsEmployeeDropdownOpen((o) => !o)}
              onBlur={() => {
                handleNoDuesFieldBlur('employeeId');
                setFormErrors((prev) => ({ ...prev, employeeId: validateField('employeeId', clearanceForm.employeeId) }));
              }}
              disabled={!!editingClearanceId}
              className={`w-full px-3 py-2 border rounded-md text-left focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has('employeeId') && formErrors.employeeId ? 'border-red-500 bg-red-50' : 'border-gray-300'} ${editingClearanceId ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
            >
              {selectedEmployee
                ? `${((selectedEmployee.fullName && !selectedEmployee.fullName.includes('undefined')) ? selectedEmployee.fullName : [selectedEmployee.personalInfo?.firstName, selectedEmployee.personalInfo?.lastName].filter(Boolean).join(' ')) || selectedEmployee.employeeId}${selectedEmployee.department ? ` - ${selectedEmployee.department}` : (selectedEmployee.employmentInfo?.department ? ` - ${selectedEmployee.employmentInfo.department}` : '')}`
                : 'Select Employee'}
            </button>
            {noDuesTouchedFields.has('employeeId') && formErrors.employeeId && (
              <p className="text-xs text-red-600 mt-1 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                {formErrors.employeeId}
              </p>
            )}
            {isEmployeeDropdownOpen && (
              <div className="relative">
                <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        value={employeeSearchTerm}
                        onChange={(e) => {
                          // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                          const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                          setEmployeeSearchTerm(value);
                        }}
                        onKeyPress={handleSearchKeyPress}
                        maxLength={100}
                        placeholder="Search employee by name, email, department..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                      />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {(eligibleEmployees || [])
                      .filter((emp) => {
                        const q = employeeSearchTerm.trim().toLowerCase();
                        if (!q) return true;
                        const name = (emp.fullName || `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`).toLowerCase();
                        const dep = (emp.department || emp.employmentInfo?.department || '').toLowerCase();
                        const email = (emp.email || emp.contactInfo?.workEmail || emp.contactInfo?.personalEmail || '').toLowerCase();
                        return name.includes(q) || dep.includes(q) || email.includes(q);
                      })
                      .map((emp) => (
                        <button
                          key={emp._id}
                          type="button"
                          onClick={() => {
                            setClearanceForm({ ...clearanceForm, employeeId: emp._id, exitRequestId: '' });
                            setIsEmployeeDropdownOpen(false);
                            setEmployeeSearchTerm('');
                            setFormErrors((prev) => ({ ...prev, employeeId: validateField('employeeId', emp._id) }));
                            handleNoDuesFieldBlur('employeeId');
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                        >
                          <div className="font-medium">
                            {(emp.fullName && !emp.fullName.includes('undefined')
                              ? emp.fullName
                              : [emp.personalInfo?.firstName, emp.personalInfo?.lastName].filter(Boolean).join(' ')) || emp.employeeId}
                          </div>
                          <div className="text-gray-500 text-xs">{emp.email || emp.contactInfo?.workEmail || emp.contactInfo?.personalEmail || ''}</div>
                          <div className="text-gray-500 text-xs">
                            {emp.department || emp.employmentInfo?.department || ''}
                            {(emp.department || emp.employmentInfo?.department) && (emp.role || emp.user?.role || emp.employmentInfo?.designation) ? ' | ' : ''}
                            {(emp.role || emp.user?.role || emp.employmentInfo?.designation || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exit Request <span className="text-red-500">*</span>
            </label>
            <select
              value={clearanceForm.exitRequestId}
              onChange={(e) => {
                const value = e.target.value;
                setFormErrors((prev) => ({ ...prev, exitRequestId: validateField('exitRequestId', value) }));
                handleExitRequestChange(e);
              }}
              onBlur={() => handleNoDuesFieldBlur('exitRequestId')}
              required
              disabled={!selectedEmployee || !!editingClearanceId}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has('exitRequestId') && formErrors.exitRequestId ? 'border-red-500 bg-red-50' : 'border-gray-300'} ${(!selectedEmployee || editingClearanceId) ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
            >
              <option value="">Select Exit Request</option>
              {exitRequestsForSelected.map(request => (
                <option key={request._id || request.id} value={request._id || request.id}>
                  {request.employeeName || selectedEmployee?.fullName} - {request.requestId}
                </option>
              ))}
            </select>
            {!selectedEmployee && (
              <p className="text-xs text-gray-500 mt-1">Please select an employee first</p>
            )}
            {noDuesTouchedFields.has('exitRequestId') && formErrors.exitRequestId && (
              <p className="text-xs text-red-600 mt-1 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                {formErrors.exitRequestId}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Working Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={clearanceForm.lastWorkingDate}
              onClick={(e) => !selectedExitRequest && e.target.showPicker?.()}
              onFocus={(e) => !selectedExitRequest && e.target.showPicker?.()}
              onChange={(e) => {
                const value = e.target.value;
                setFormErrors((prev) => ({ ...prev, lastWorkingDate: validateField('lastWorkingDate', value) }));
                setClearanceForm({ ...clearanceForm, lastWorkingDate: value });
              }}
              onBlur={() => {
                handleNoDuesFieldBlur('lastWorkingDate');
                setFormErrors((prev) => ({ ...prev, lastWorkingDate: validateField('lastWorkingDate', clearanceForm.lastWorkingDate) }));
              }}
              required
              readOnly={!!selectedExitRequest}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has('lastWorkingDate') && formErrors.lastWorkingDate ? 'border-red-500 bg-red-50' : 'border-gray-300'} ${selectedExitRequest ? 'bg-gray-100' : ''}`}
            />
            {selectedExitRequest && (
              <p className="text-xs text-gray-500 mt-1">Auto-filled from Exit Request {selectedExitRequest.requestId} (locked)</p>
            )}
            {noDuesTouchedFields.has('lastWorkingDate') && formErrors.lastWorkingDate && (
              <p className="text-xs text-red-600 mt-1 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                {formErrors.lastWorkingDate}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clearance Type <span className="text-red-500">*</span>
            </label>
            <select
              value={clearanceForm.clearanceType}
              onChange={(e) => {
                const value = e.target.value;
                setFormErrors((prev) => ({ ...prev, clearanceType: validateField('clearanceType', value) }));
                setClearanceForm({ ...clearanceForm, clearanceType: value });
              }}
              onBlur={() => handleNoDuesFieldBlur('clearanceType')}
              required
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has('clearanceType') && formErrors.clearanceType ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
            >
              <option value="resignation">Resignation</option>
              <option value="termination">Termination</option>
              <option value="retirement">Retirement</option>
              <option value="contract_end">Contract End</option>
            </select>
            {noDuesTouchedFields.has('clearanceType') && formErrors.clearanceType && (
              <p className="text-xs text-red-600 mt-1 flex items-center">
                <AlertCircle className="h-3 w-3 mr-1" />
                {formErrors.clearanceType}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={clearanceForm.priority}
              onChange={(e) => setClearanceForm({ ...clearanceForm, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        {/* Clearance Items Section */}
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Clearance Items</h4>
          <div className="space-y-4">
            {clearanceForm.clearanceItems.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={item.category || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const newItems = [...clearanceForm.clearanceItems];
                      newItems[index] = { ...newItems[index], category: value };
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                      setFormErrors((prev) => ({ ...prev, [`clearanceItems.${index}.category`]: validateField('category', value) }));
                    }}
                    onBlur={() => {
                      handleNoDuesFieldBlur(`clearanceItems.${index}.category`);
                      setFormErrors((prev) => ({ ...prev, [`clearanceItems.${index}.category`]: validateField('category', item.category) }));
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has(`clearanceItems.${index}.category`) && formErrors[`clearanceItems.${index}.category`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                  >
                    <option value="">Select Category</option>
                    <option value="IT_Assets">IT Assets (Laptops, Computers, Software)</option>
                    <option value="Office_Equipment">Office Equipment (Keys, Supplies)</option>
                    <option value="Documents">Documents (Confidential Files)</option>
                    <option value="Financial">Financial (Outstanding Payments)</option>
                    <option value="Access_Cards">Access Cards (Building, Parking)</option>
                    <option value="Vehicle">Vehicle (Company Car, Parking)</option>
                    <option value="Other">Other Assets</option>
                  </select>
                  {noDuesTouchedFields.has(`clearanceItems.${index}.category`) && formErrors[`clearanceItems.${index}.category`] && (
                    <p className="text-xs text-red-600 mt-1 flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {formErrors[`clearanceItems.${index}.category`]}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={item.itemName || ''}
                    maxLength={100}
                    onKeyPress={handleTextKeyPress}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Filter out invalid characters (alphanumeric, spaces, commas only)
                      value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                      // Enforce max length
                      if (value.length > 100) {
                        value = value.substring(0, 100);
                      }
                      const newItems = [...clearanceForm.clearanceItems];
                      newItems[index] = { ...newItems[index], itemName: value };
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                      setFormErrors((prev) => ({ ...prev, [`clearanceItems.${index}.itemName`]: validateField('itemName', value) }));
                    }}
                    onBlur={() => handleNoDuesFieldBlur(`clearanceItems.${index}.itemName`)}
                    placeholder="e.g., Laptop, Office Keys"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has(`clearanceItems.${index}.itemName`) && formErrors[`clearanceItems.${index}.itemName`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                  />
                  <div className="flex justify-between items-start mt-1">
                    <div className="flex-1">
                      {noDuesTouchedFields.has(`clearanceItems.${index}.itemName`) && formErrors[`clearanceItems.${index}.itemName`] && (
                        <p className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {formErrors[`clearanceItems.${index}.itemName`]}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 ml-auto whitespace-nowrap">
                      {(item.itemName || '').length} / 100 characters
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Value (₹)</label>
                  <input
                    type="number"
                    value={item.itemValue || ''}
                    onKeyDown={handleNumberKeyDown}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Remove any non-numeric characters
                      value = value.replace(/[^0-9]/g, '');
                      // Ensure value is 0 or greater
                      if (value && Number(value) < 0) {
                        value = '0';
                      }
                      // Enforce max value (1 billion)
                      if (value && Number(value) > 1000000000) {
                        value = '1000000000';
                      }
                      const numValue = value ? parseFloat(value) : 0;
                      const newItems = [...clearanceForm.clearanceItems];
                      newItems[index] = { ...newItems[index], itemValue: numValue };
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                      setFormErrors((prev) => ({
                        ...prev,
                        [`clearanceItems.${index}.itemValue`]: numValue < 0 ? 'Value cannot be negative' : ''
                      }));
                    }}
                    onBlur={() => handleNoDuesFieldBlur(`clearanceItems.${index}.itemValue`)}
                    placeholder="0"
                    min="0"
                    max="1000000000"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${noDuesTouchedFields.has(`clearanceItems.${index}.itemValue`) && formErrors[`clearanceItems.${index}.itemValue`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                  />
                  <div className="flex justify-between items-start mt-1">
                    <div className="flex-1">
                      {noDuesTouchedFields.has(`clearanceItems.${index}.itemValue`) && formErrors[`clearanceItems.${index}.itemValue`] && (
                        <p className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {formErrors[`clearanceItems.${index}.itemValue`]}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 ml-auto whitespace-nowrap">
                      Value: ₹{formatIndianNumber(item.itemValue || 0)}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                  <input
                    type="text"
                    value={item.itemSerialNumber || ''}
                    maxLength={50}
                    onKeyPress={handleTextKeyPress}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Filter out invalid characters (alphanumeric, spaces, commas only)
                      value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
                      // Enforce max length
                      if (value.length > 50) {
                        value = value.substring(0, 50);
                      }
                      const newItems = [...clearanceForm.clearanceItems];
                      newItems[index] = { ...newItems[index], itemSerialNumber: value };
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                    }}
                    placeholder="e.g., SN123456789"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  />
                  <div className="flex justify-end mt-1">
                    <p className="text-xs text-gray-500">
                      {(item.itemSerialNumber || '').length} / 50 characters
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                  <select
                    value={item.itemCondition || 'good'}
                    onChange={(e) => {
                      const newItems = [...clearanceForm.clearanceItems];
                      newItems[index] = { ...newItems[index], itemCondition: e.target.value };
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = clearanceForm.clearanceItems.filter((_, i) => i !== index);
                      setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
                    }}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                const newItems = [...clearanceForm.clearanceItems, {
                  category: '',
                  itemName: '',
                  itemDescription: '',
                  itemValue: 0,
                  itemSerialNumber: '',
                  itemCondition: 'good',
                  status: 'pending',
                  isReplacementRequired: false,
                  replacementCost: 0
                }];
                setClearanceForm({ ...clearanceForm, clearanceItems: newItems });
              }}
              className="flex items-center px-4 py-2 text-sm font-medium text-[#0F2942] bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Clearance Item
            </button>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => {
              setShowClearanceForm(false);
              resetClearanceForm();
            }}
            className="btn-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createClearanceMutation.isLoading || updateClearanceMutation.isLoading}
            className="btn-create"
          >
            {editingClearanceId
              ? (updateClearanceMutation.isLoading ? 'Updating...' : 'Update Clearance')
              : (createClearanceMutation.isLoading ? 'Creating...' : 'Create Clearance')}
          </button>
          {formErrors.submit && (
            <p className="text-xs text-red-600 mt-2">{formErrors.submit}</p>
          )}
        </div>
      </form >
    </div >
  );

  // Render clearance details
  const renderClearanceDetails = () => {
    if (!selectedClearance) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No clearance selected</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Clearance Details</h3>
              <button
                onClick={() => setSelectedClearance(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Employee Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedClearance.employeeName}</p>
                  <p><span className="font-medium">Department:</span> {selectedClearance.department}</p>
                  <p><span className="font-medium">Position:</span> {selectedClearance.position}</p>
                  <p><span className="font-medium">Email:</span> {selectedClearance.employeeEmail}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Clearance Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Clearance ID:</span> {selectedClearance.clearanceId}</p>
                  <p><span className="font-medium">Status:</span>
                    <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedClearance.clearanceStatus)}`}>
                      {getStatusIcon(selectedClearance.clearanceStatus)}
                      <span className="ml-1">{selectedClearance.clearanceStatus}</span>
                    </span>
                  </p>
                  <p><span className="font-medium">Type:</span> {selectedClearance.clearanceType}</p>
                  <p><span className="font-medium">Last Working Date:</span> {formatDate(selectedClearance.lastWorkingDate, { includeTime: false })}</p>
                </div>
              </div>
            </div>

            {selectedClearance.clearanceItems && selectedClearance.clearanceItems.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Clearance Items</h4>
                <div className="space-y-3">
                  {selectedClearance.clearanceItems.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-sm text-gray-600">{item.itemDescription}</p>
                          <p className="text-sm text-gray-500">Category: {item.category}</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.status === 'completed' ? 'bg-green-100 text-green-800' :
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render clearances list
  // Export clearances to CSV
  const handleExport = () => {
    try {
      const headers = ['Employee Name', 'Department', 'Status', 'Clearance ID', 'Last Working Date', 'Created Date'];
      const csvContent = [
        headers.join(','),
        ...clearances.map(clearance => [
          clearance.employeeName || '',
          clearance.department || '',
          clearance.clearanceStatus || '',
          clearance.clearanceId || '',
          clearance.lastWorkingDate ? formatDate(clearance.lastWorkingDate, { includeTime: false }) : '',
          clearance.createdAt ? formatDate(clearance.createdAt, { includeTime: false }) : ''
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `no_dues_clearances_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      alert('Failed to export clearances');
    }
  };

  const renderClearancesList = () => (
    <div className="space-y-6">
      {/* KPI Cards - Moved from Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {(() => {
          const KpiCard = ({ icon: Icon, title, value, color }) => {
            let bgColor = "bg-[#0F2942]";
            if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
            else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
            else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
            else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
            else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

            return (
              <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
                <div className="space-y-4 flex-1">
                  <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
                    {title}
                  </h3>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
                      {value}
                    </span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            );
          };

          return (
            <>
              <KpiCard
                icon={FileText}
                title="Total Clearances"
                value={formatIndianNumber(stats.totalClearances || 0)}
                color="bg-[#0F2942]"
              />
              <KpiCard
                icon={Clock}
                title="Pending Clearances"
                value={formatIndianNumber(stats.pendingClearances || 0)}
                color="bg-amber-600"
              />
              <KpiCard
                icon={Timer}
                title="In-Progress Clearances"
                value={formatIndianNumber(stats.inProgressClearances || 0)}
                color="bg-[#0F2942]"
              />
              <KpiCard
                icon={CheckCircle}
                title="Completed Clearances"
                value={formatIndianNumber(stats.completedClearances || 0)}
                color="bg-emerald-600"
              />
            </>
          );
        })()}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search Bar */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={handleSearchKeyPress}
              maxLength={100}
              placeholder="Search by Employee or Email ID ..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  // Maintain focus on input
                  const input = document.activeElement;
                  if (input && input.tagName === 'INPUT') {
                    setTimeout(() => input.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Department Filter */}
          <div className="flex-shrink-0 w-48">
            <select
              value={departmentFilter}
              onChange={(e) => { setDepartmentFilter(e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60 truncate"
            >
              <option value="all">All Departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={setDateRange}
              containerClassName="flex items-center space-x-2"
              inputClassName="input-field text-sm w-36 cursor-pointer focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            />
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter !== 'all' || departmentFilter !== 'all' || startDate || endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  resetNoDuesFilters();
                  setSortConfig({ key: null, direction: 'asc' });
                }}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Clearances Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">No-Dues Clearances</h3>
        </div>

        {isClearancesLoading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading...</span>
          </div>
        ) : filteredClearances.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No clearances found</p>
          </div>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50"
                        checked={selectedRows.size === filteredClearances.length && filteredClearances.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('employeeName')}>
                      <div className="flex items-center space-x-1">
                        <span>Employee</span>
                        {sortConfig.key === 'employeeName' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('department')}>
                      <div className="flex items-center space-x-1">
                        <span>Department</span>
                        {sortConfig.key === 'department' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('clearanceType')}>
                      <div className="flex items-center space-x-1">
                        <span>Type</span>
                        {sortConfig.key === 'clearanceType' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lastWorkingDate')}>
                      <div className="flex items-center space-x-1">
                        <span>Last Working Date</span>
                        {sortConfig.key === 'lastWorkingDate' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('clearanceStatus')}>
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortConfig.key === 'clearanceStatus' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>
                      <div className="flex items-center space-x-1">
                        <span>Created At</span>
                        {sortConfig.key === 'createdAt' ? (
                          sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredClearances.map((clearance) => (
                    <tr key={clearance._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50"
                          checked={selectedRows.has(clearance._id)}
                          onChange={() => handleSelectRow(clearance)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{clearance.employeeName}</div>
                            <div className="text-sm text-gray-500">{clearance.employeeEmail}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{clearance.department}</div>
                        <div className="text-sm text-gray-500">{clearance.position}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full capitalize">
                          {clearance.clearanceType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(clearance.lastWorkingDate, { includeTime: false })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(clearance.clearanceStatus)}`}>
                            {getStatusIcon(clearance.clearanceStatus)}
                            <span className="ml-1 capitalize">{clearance.clearanceStatus}</span>
                          </span>
                          {clearance.clearanceStatus === 'pending' && canDeleteClearance && (
                            <span className="text-xs text-gray-500" title="This clearance can be deleted">
                              🗑️
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(clearance.createdAt, { includeTime: true })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <PermissionGate permission="exit_management" action="read">
                            <button
                              onClick={() => handleViewClearance(clearance)}
                              className="text-[#0F2942] hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="exit_management" action="update">
                            {clearance.clearanceStatus !== 'completed' && (
                              <button
                                onClick={() => handleEditClearance(clearance)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit Clearance"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                          </PermissionGate>
                          {clearance.clearanceStatus === 'in_progress' && (
                            <PermissionGate permission="exit_management" action="update">
                              <button
                                onClick={() => handleApproveClearance(clearance._id)}
                                className="text-green-600 hover:text-green-900"
                                title="Approve"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          {clearance.clearanceStatus === 'pending' && (
                            <PermissionGate permission="exit_management" action="delete">
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setPendingDeleteId(clearance._id)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete (Only pending clearances can be deleted)"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </PermissionGate>
                          )}
                          <PermissionGate permission="exit_management" action="update">
                            {clearance.clearanceStatus !== 'completed' && (
                              <select
                                value={clearance.clearanceStatus}
                                onChange={(e) => handleStatusUpdate(clearance._id, e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white min-w-[100px]"
                                title="Update Status"
                              >
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            )}
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={clearancesPage}
              totalPages={clearancesTotalPages}
              totalItems={clearancesTotalItems}
              limit={clearancesPageSize}
              hasNextPage={clearancesPage < clearancesTotalPages}
              hasPrevPage={clearancesPage > 1}
              onPageChange={(p) => setClearancesPage(p)}
              onLimitChange={(l) => { setClearancesPageSize(l); }}
              limitOptions={[10, 20, 50]}
              border={false}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">No-Dues Clearance</h1>
          <p className="text-gray-600">Manage employee clearance process and asset returns</p>
        </div>
        <div className="flex items-center space-x-4">
          <PermissionGate permission="exit_management" action="delete">
            {selectedRows.size > 0 && (
              <Button
                variant="danger"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white"
              >
                {isBulkDeleting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>Delete Selected ({selectedRows.size})</span>
              </Button>
            )}
          </PermissionGate>

          <PermissionGate permission="exit_management" action="read">
            <Button
              variant="outline"
              onClick={handleExportClick}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>{selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}</span>
            </Button>
          </PermissionGate>
          <PermissionGate permission="exit_management" action="create">
            <button
              onClick={() => setShowClearanceForm(true)}
              className="btn-cancel flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Clearance
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Content */}
      {showClearanceForm ? renderClearanceForm() : (
        selectedClearance ? renderClearanceDetails() : renderClearancesList()
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        onExportRange={handleExportRange}
        loading={exportLoading}
        title="Export No-Dues Data"
        showDateRange={true}
      />

      {/* Delete Confirmation Modal Overlay */}
      {pendingDeleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-center text-gray-900 mb-2">Confirm Delete</h3>
              <p className="text-sm text-center text-gray-500 mb-6">
                Are you sure you want to delete this clearance? This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={() => setPendingDeleteId(null)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0F2942]/50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteClearance(pendingDeleteId, true)}
                  disabled={deleteClearanceMutation.isLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleteClearanceMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoDues;
